import { useState } from 'react';
import type { ReplicaCode } from '../types.ts';

// 10-char replica code chip with copy + verified affordances. Empty state
// is a dashed-rail "no codes for this query" — never fabricate.
export function ReplicaCodeChip({ code }: { code: ReplicaCode }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  function copy() {
    navigator.clipboard?.writeText(code.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  }

  return (
    <div
      className="vb-card"
      style={{
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          className="mono tabular"
          style={{
            color: 'var(--fg-1)',
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: '0.05em',
            flex: 1,
          }}
        >
          {code.code}
        </span>
        <span
          className="caps mono"
          style={{
            fontSize: 9,
            padding: '1px 5px',
            borderRadius: 2,
            border: '1px solid',
            borderColor: code.verified ? 'rgba(136,176,122,0.40)' : 'rgba(201,168,90,0.40)',
            background: code.verified ? 'rgba(136,176,122,0.10)' : 'rgba(201,168,90,0.10)',
            color: code.verified ? 'var(--pos)' : 'var(--warn)',
            letterSpacing: '0.08em',
          }}
        >
          {code.verified ? '✓ verified' : `conf ${code.confidence.toFixed(2)}`}
        </span>
        <button
          type="button"
          onClick={copy}
          className="caps mono"
          style={{
            fontSize: 9,
            padding: '2px 7px',
            border: '1px solid var(--line-1)',
            borderRadius: 2,
            background: 'var(--bg-2)',
            color: copied ? 'var(--pos)' : 'var(--fg-2)',
            letterSpacing: '0.08em',
          }}
        >
          {copied ? 'copied' : 'copy'}
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{ color: 'var(--fg-4)', fontSize: 12 }}
          aria-label={expanded ? 'collapse' : 'expand'}
        >
          {expanded ? '▾' : '▸'}
        </button>
      </div>
      {expanded && (
        <div
          className="mono"
          style={{ color: 'var(--fg-3)', fontSize: 11, lineHeight: 1.4, marginTop: 4 }}
        >
          {code.context}
          {code.source_url && (
            <>
              {' '}
              <a
                href={code.source_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--accent-2)' }}
              >
                source ↗
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function NoReplicaCodes() {
  return (
    <div
      className="mono"
      style={{
        padding: '12px 14px',
        border: '1px dashed var(--line-2)',
        borderRadius: 4,
        color: 'var(--fg-3)',
        fontSize: 12,
        textAlign: 'center',
      }}
    >
      no replica codes in corpus for this query yet
    </div>
  );
}
